<template>
  <base-resize-observer @resize="onContainerResize">
    <div
      ref="scrollable"
      class="base-virtual-scroll"
      @scroll="onScroll"
    >
      <base-virtual-scroll-viewport
        :data-key="dataKey"
        :visible-items="visibleItems"
        :is-loading="isLoading"
        :is-header-visible="isHeaderVisible"
        :is-footer-visible="isFooterVisible"
        :offset-top-px="offsetTopPx"
        :offset-bottom-px="offsetBottomPx"
        :offset-top-items="offsetTopItems"
        :get-item-size="VS.getItemSize"
        :render-delay="renderDelay"
        @item-activation="onItemActivation"
        @item-deactivation="onItemDeactivation"
        @item-resize="onItemResize"
        @header-slot-resize="onHeaderSlotResize"
        @footer-slot-resize="onFooterSlotResize"
      >
        <template #default="props">
          <slot
            name="default"
            v-bind="props"
          />
        </template>

        <template #placeholder>
          <slot name="placeholder" />
        </template>

        <template #header>
          <slot name="header" />
        </template>

        <template #footer>
          <slot name="footer" />
        </template>

        <template #loader>
          <slot name="loader" />
        </template>
      </base-virtual-scroll-viewport>
    </div>
  </base-resize-observer>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue-demi';

import { getTimer } from '@/common';
import { animateScrollTo } from '../../../helpers';
import BaseVirtualScrollViewport from './BaseVirtualScrollViewportV2.vue';
import { BaseVirtualScroll } from './base-virtual-scroll';
import { BaseResizeObserver } from '../../decorators';
import {
  IVScrollItem,
  IVScrollSlot,
  IResizeInfo,
  IVScrollPosition,
} from '../../../types';
import {
  DEFAULT_ITEM_SIZE,
  DEFAULT_BUFFER_SIZE,
  DEFAULT_RENDER_DELAY,
  DEFAULT_SCROLL_ANIMATION_DURATION,
} from '../../../constants';
import { getItemIndices } from './helpers';

import '@onereach/styles/scrollbar.css';

export default defineComponent({
  name: 'BaseVirtualScrollV2',
  components: {
    BaseVirtualScrollViewport,
    BaseResizeObserver,
  },
  props: {
    items: {
      type: Array as PropType<IVScrollItem[]>,
      default: (): IVScrollItem[] => [],
    },
    /** Amount of items that can be rendered before and after viewport */
    buffer: {
      type: Number,
      default: DEFAULT_BUFFER_SIZE,
    },
    dataKey: {
      type: String,
      default: 'id',
    },
    defaultItemSize: {
      type: Number,
      default: DEFAULT_ITEM_SIZE,
    },
    renderDelay: {
      type: Number,
      default: DEFAULT_RENDER_DELAY,
    },
    scrollAnimationDuration: {
      type: Number,
      default: DEFAULT_SCROLL_ANIMATION_DURATION,
    },
    pinnedItemId: {
      type: String as PropType<string | number>,
      default: '',
    },
    pinnedItemPosition: {
      type: String as PropType<IVScrollPosition>,
      default: 'top',
    },
    /* NOTE: for triggering onbottom event on scroll */
    bottomThreshold: {
      type: Number,
      default: 0,
    },
    /* NOTE: for swapping bottom scroll compensation with top if pinned item is not fully in the viewport
    (see `forcePinnedPosition()`) */
    bottomOffset: {
      type: Number,
      default: 0,
    },
    isLoading: Boolean,
  },
  emits: [
    'item-resize',
    'item-activation',
    'viewport-resize',
    'scroll',
  ],
  data() {
    const VS = new BaseVirtualScroll({
      items: this.$props.items,
      itemIndicesById: getItemIndices(this.$props.items, this.dataKey),
      dataKey: this.$props.dataKey,
      defaultItemSize: this.$props.defaultItemSize,
      buffer: this.$props.buffer,
    });
    return {
      VS,
      scrollTop: 0,
      visibleItems: [] as IVScrollItem[],
      offsetTopPx: 0,
      offsetTopItems: 0,
      offsetBottomPx: 0,
      offsetBottomItems: 0,
      isHeaderVisible: false,
      isFooterVisible: false,
      scrollableElement: null as HTMLElement | null,
    };
  },

  computed: {
    isPinnedItemVisible() {
      if (!this.pinnedItemId) return false;
      return this.visibleItems.find(it => it[this.dataKey] === this.pinnedItemId);
    },
  },

  watch: {
    items(newItems, oldItems) {
      this.onItemsUpdate(oldItems, newItems);
    },

    pinnedItemId: {
      handler(value: string | number) {
        this.VS.setPinnedItemId(value);
        if (this.pinnedItemPosition !== this.VS.pinnedItemPosition) {
          /* NOTE: In some cases the change to pinned position can be forced inside setScrollToItem method.
          Need to sync it with BaseVirtualScrollV2 as soon as pinned item has been changed */
          this.VS.setPinnedItemPosition(this.pinnedItemPosition);
        }
      },
      immediate: true,
    },

    pinnedItemPosition: {
      handler(value: IVScrollPosition) {
        this.VS.setPinnedItemPosition(value);
      },
      immediate: true,
    },

    async scrollTop(value: number) {
      await this.$nextTick();
      if (this.scrollableElement) {
        this.scrollableElement.scrollTop = value;
      }
    },
  },

  mounted() {
    this.scrollableElement = this.$refs['scrollable'] as HTMLElement;
  },

  methods: {
    onItemsUpdate(oldItems: IVScrollItem[], newItems: IVScrollItem[]) {
      const state = this.VS.setItems(oldItems, newItems);
      Object.assign(this, state);
    },

    onContainerResize(size: IResizeInfo) {
      const { height } = size;
      const state = this.VS.setViewportSize(height);
      Object.assign(this, state);
      this.$emit('viewport-resize', height);
    },

    forcePinnedPosition() {
    /* NOTE: with bottom positioning we want to avoid bottom-based scroll compensation
      if pinned item is scrolled up and its bottom line is out of visible area.
        Example: ConversationHistory with pinned session higher than viewport;
        scroll should be compensated only if user is at the bottom, i.e. interacts with the chatbox,
        but if session is scrolled up and new events are appended to the end,
        scroll compensation would cause upper content jump.
      */
      if (this.pinnedItemPosition === 'bottom' && this.pinnedItemId) {
        const pinnedBottomPx = this.VS.getItemOffset({ id: this.pinnedItemId, position: 'bottom' });
        if (pinnedBottomPx > this.scrollTop + this.VS.viewportSize + this.bottomOffset) {
          this.VS.setPinnedItemPosition('top');
        } else {
          this.VS.setPinnedItemPosition('bottom');
        }
      }
    },

    async onScroll(event: MouseEvent) {
      /* NOTE: the timeout helps to overcome side-effects of Chrome firing scroll event
      when scrollable element is downsized close to the bottom scroll position.
      Example of the side-effect:
        at the bottom scroll position, after an element up the bottom-pinned is deleted,
        the container is scrolled up a bit
        (can be observed in Storybook -> Dynamic Items -> Add And Delete Interactive) */
      await getTimer(200);

      const state = this.VS.setScrollTop((event.target as HTMLElement).scrollTop);
      /* NOTE: scrollTop watcher would be triggered at this point */
      Object.assign(this, state);

      this.$emit('scroll', { state });
      const clientSize = this.scrollableElement!.clientHeight;
      const scrollSize = this.scrollableElement!.scrollHeight;
      if (!this.isLoading && this.scrollTop + clientSize + this.bottomThreshold >= scrollSize) {
        this.$emit('tobottom');
      }
      this.forcePinnedPosition();
    },

    onItemActivation(item: IVScrollItem): void {
      const id = item[this.dataKey];
      this.$emit('item-activation', { id });
    },

    onItemDeactivation(item: IVScrollItem): void {
      const id = item[this.dataKey];
      this.$emit('item-deactivation', { id });
    },

    async onItemResize(args: {
      resizeInfo: IResizeInfo;
      item: IVScrollItem;
    }) {
      /* Ignore events when item is being hidden */
      if (args.resizeInfo.height === 0) {
        return;
      }
      const { item, resizeInfo } = args;
      const id = item[this.dataKey];
      const { height } = resizeInfo;
      const params = { id, size: height };
      const state = this.VS.setItemSize({
        resizedItemId: id,
        size: height,
        pinnedItemId: this.pinnedItemId,
      });
      Object.assign(this, state);
      await this.$nextTick();
      this.$emit('item-resize', params);

      if (args.item[this.dataKey] === this.pinnedItemId) {
        this.forcePinnedPosition();
      }
    },

    async onSlotResize({ size, slot }: { size: number; slot: IVScrollSlot; }) {
      /* Ignore events when item is being hidden, i.e. size === 0 */
      if (!size) return;
      const state = this.VS.setSlotSize({ size, slot });
      Object.assign(this, state);
      await this.$nextTick();
      this.$emit('item-resize', { size, slot });
    },

    onHeaderSlotResize(resizeInfo: IResizeInfo) {
      this.onSlotResize({ size: resizeInfo.height, slot: IVScrollSlot.HEADER });
    },

    onFooterSlotResize(resizeInfo: IResizeInfo) {
      this.onSlotResize({ size: resizeInfo.height, slot: IVScrollSlot.FOOTER });
    },

    async scrollToItem(id: string | number, position?: IVScrollPosition) {
      const localPosition = position ?? this.pinnedItemPosition;
      this.VS.setPinnedItemPosition(localPosition);
      const state = this.VS.setScrollToItem({ id });
      Object.assign(this, state);
      /* NOTE: VS.setScrollToItem can force the change of pinned position if item cannot be placed top or bottom as required.
      These ensures correct respond of scroll to items resize right after scrolling,
      but then we need to restore initial value */
      await getTimer(1000);
      this.VS.setPinnedItemPosition(localPosition);
    },

    async scrollToItemAnimated(id: string | number, position: IVScrollPosition = 'top') {
      this.VS.setPinnedItemId(id);
      this.VS.setPinnedItemPosition(position);
      const state = this.VS.setScrollToItem({ id });
      return await animateScrollTo(this.scrollableElement!, state.scrollTop, this.scrollAnimationDuration);
    },
  },
});
</script>

<style lang="scss" scoped>

.base-virtual-scroll {
  height: 100%;
  overflow: auto;
  overflow-anchor: none;
}
</style>
